import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Radar, Loader2 } from "lucide-react";
import { toast } from "sonner";

const FIELDS = [
  ["moving_interval_s", "Update every (s) while moving"], ["parked_interval_s", "Update every (s) while parked"],
  ["stale_after_s", "Mark stale after (s)"], ["offline_after_s", "Mark offline after (s)"],
  ["retention_days", "Keep location history (days)"], ["min_server_interval_s", "Reject updates faster than (s)"],
];

export const TrackingPolicyCard = () => {
  const { hasPerm } = useAuth();
  const allowed = hasPerm("settings:manage");
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (allowed) api.get("/tracking/policy").then((r) => setP(r.data)).catch(() => {}); }, [allowed]);
  if (!allowed || !p) return null;
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/tracking/policy", p); setP(data); toast.success("Tracking policy saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border border-border rounded-lg bg-card p-6 mt-6" data-testid="tracking-policy-card">
      <div className="flex items-center gap-2 mb-1"><Radar className="w-4 h-4 text-brand" /><h2 className="font-heading font-semibold">Driver Tracking Policy</h2></div>
      <p className="text-sm text-muted-foreground mb-4">Applies to on-shift drivers only. Location older than the retention period is deleted daily.</p>
      <div className="grid sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, l]) => (
          <div key={k} className="space-y-1.5"><Label className="text-xs">{l}</Label>
            <Input type="number" value={p[k]} onChange={(e) => setP({ ...p, [k]: e.target.value })} data-testid={`policy-${k}`} /></div>
        ))}
        <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">If location permission is denied</Label>
          <select className="w-full h-9 rounded-md border border-input bg-transparent px-2 text-sm" value={p.denied_policy} onChange={(e) => setP({ ...p, denied_policy: e.target.value })} data-testid="policy-denied">
            <option value="ALLOW_AND_ALERT">Allow clock in, mark “Permission denied” and alert dispatch</option>
            <option value="BLOCK_CLOCK_IN">Block clock in until location is allowed</option>
          </select></div>
      </div>
      <Button className="mt-4" onClick={save} disabled={busy} data-testid="policy-save">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Save Policy"}</Button>
    </div>
  );
};
